import { Head, useForm, usePage } from "@inertiajs/react"

import { VoicePicker } from "@/components/agents/setup/receptionist/voice-picker"
import type { VoiceOption } from "@/components/agents/setup/types"
import { FormField, FormFields } from "@/components/form-field"
import HeadingSmall from "@/components/heading-small"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { cn } from "@/lib/utils"
import { settingsIdentityPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

// IMP-456: Settings → Identity — the account-level facts every job inherits
// (voice, communication style, AI disclosure). One place to change them; no
// job carries its own copy.

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Settings", href: settingsIdentityPath() },
]

const STYLE_OPTIONS = [
  {
    value: "professional",
    label: "Professional",
    sub: "Polished and precise. Best for healthcare, finance, or legal.",
  },
  {
    value: "warm",
    label: "Warm",
    sub: "Friendly and genuine. Works for most local businesses.",
  },
  {
    value: "conversational",
    label: "Conversational",
    sub: "Relaxed and natural. Great for hospitality or consumer brands.",
  },
]

interface Identity {
  assistant_voice_id: string
  communication_style: string
  disclose_ai: boolean
}

export default function IdentitySettings() {
  const { identity, voices } = usePage<{
    identity: Identity
    voices: VoiceOption[]
  }>().props

  const { data, setData, patch, processing, errors } = useForm({
    account: {
      assistant_voice_id: identity.assistant_voice_id,
      communication_style: identity.communication_style,
      disclose_ai: identity.disclose_ai,
    },
  })

  function updateField<K extends keyof typeof data.account>(
    key: K,
    value: (typeof data.account)[K],
  ) {
    setData("account", { ...data.account, [key]: value })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(settingsIdentityPath(), { preserveScroll: true })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Identity" />

      <SettingsLayout>
        <form onSubmit={handleSubmit} className="space-y-10">
          <div className="space-y-6">
            <HeadingSmall
              title="Identity"
              description="How your assistant sounds and communicates, across every job."
            />

            <FormFields>
              <FormField
                label="Voice"
                sublabel="How your assistant sounds to your callers."
                error={(errors as Record<string, string>)?.assistant_voice_id}
              >
                <VoicePicker
                  voices={voices}
                  selected={data.account.assistant_voice_id}
                  onSelect={(id) => updateField("assistant_voice_id", id)}
                />
              </FormField>

              <FormField
                label="Communication style"
                sublabel="How your assistant writes and speaks everywhere, such as calls, texts, and chat."
                error={(errors as Record<string, string>)?.communication_style}
              >
                <div className="grid grid-cols-3 gap-2">
                  {STYLE_OPTIONS.map((opt) => {
                    const selected =
                      data.account.communication_style === opt.value
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() =>
                          updateField("communication_style", opt.value)
                        }
                        className={cn(
                          "rounded-lg border p-4 text-left transition-colors",
                          selected
                            ? "border-blue-300 bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/60 dark:to-blue-900/20"
                            : "border-border hover:bg-muted/30",
                        )}
                      >
                        <p
                          className={cn(
                            "text-sm leading-tight font-semibold",
                            selected && "text-blue-600 dark:text-blue-400",
                          )}
                        >
                          {opt.label}
                        </p>
                        <p
                          className={cn(
                            "mt-0.5 text-sm leading-relaxed",
                            selected
                              ? "text-blue-600 dark:text-blue-400"
                              : "text-muted-foreground",
                          )}
                        >
                          {opt.sub}
                        </p>
                      </button>
                    )
                  })}
                </div>
              </FormField>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium">
                    Disclose AI assistance
                  </div>
                  <div className="text-muted-foreground mt-1 text-sm">
                    If enabled, your assistant introduces itself as an AI
                    assistant. Required in some regions.
                  </div>
                </div>
                <Switch
                  checked={data.account.disclose_ai}
                  onCheckedChange={(v) => updateField("disclose_ai", v)}
                  className="shrink-0"
                />
              </div>
            </FormFields>
          </div>

          <Button size="sm" className="rounded-sm" loading={processing}>
            Save changes
          </Button>
        </form>
      </SettingsLayout>
    </AppLayout>
  )
}
